import { getMobileBookingProgress } from '@components/BookingFlow/Common/bookingFlowProgress';
import { SelectCars } from '@components/BookingFlow/Common/Car/SelectCars';
import { BookingScreen } from '@components/BookingFlow/Mobile/BookingScreen';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { t } from '@lingui/core/macro';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiBasicCard, TablePagination, usePaginationQueryParam } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import {
  commonTrackingEvents,
  tracking,
  useBookingActions,
  useBookingCars,
  useIsCarStepValid,
  useShouldFireRequestForLoggedInUser
} from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { useBookingCarSearch } from '../../../../hooks/useBookingCarSearch';
import { useMobileBookingCustomer } from '../../../../hooks/useMobileBookingCustomer';
import { getCommonTranslations } from '../../../../utils/commonTranslations';

const initialPageSize = 5;

export const Cars = () => {
  const isCarStepValid = useIsCarStepValid();
  const navigate = useNavigate();

  const { userGroupId } = useMobileBookingCustomer();
  const shouldFireRequest = useShouldFireRequestForLoggedInUser();
  const { pagination, setPagination } = usePaginationQueryParam({
    initialPageSize
  });
  const { addCar, removeCar } = useBookingActions();
  const selectedCars = useBookingCars();
  const carSearch = useBookingCarSearch({ addCar, selectedCars, userGroupId });
  const { canSubmit: hasTypedPlate, isSearching } = carSearch.plateSearch;

  // need to use isLoading here, not isPending if not page doesn't work for not logged in users
  const {
    isLoading: isSavedCarsLoading,
    data: savedCars,
    error: savedCarsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedCarsForBookingUserGroups,
    input: {
      userGroupId: userGroupId as number,
      ...pagination
    },
    queryConfig: {
      enabled: shouldFireRequest,
      // Keep the current page on screen while the next loads; without this every page change
      // re-enters the loading state and the car list blanks.
      placeholderData: keepPreviousData
    }
  });

  async function onNavigateToNextStep() {
    // A complete plate the customer typed but did not add counts as their pick.
    const addedTypedCar = hasTypedPlate && !!(await carSearch.submitTypedPlate());
    if (hasTypedPlate && !addedTypedCar) {
      return;
    }
    tracking.track(commonTrackingEvents.carStepCompleted, {
      numberOfCars: selectedCars.length + (addedTypedCar ? 1 : 0)
    });
    navigate(customerRoutes.newBookingPrimaryItems.getPath());
  }

  return (
    <BookingScreen
      progress={getMobileBookingProgress('car')}
      // oxlint-disable-next-line typescript/no-misused-promises -- the plate lookup's refetch never rejects; a failed lookup renders from the query's error
      onNextClicked={onNavigateToNextStep}
      disableNextButton={!isCarStepValid && !hasTypedPlate}
      nextButtonLoading={isSearching}
      validationVariant='carSelectionRequired'
      title={t`Add one or more cars`}
      hideShoppingCart
      showSelectedCars
    >
      {savedCarsError ? (
        <ApiErrorMessageWithTrans error={savedCarsError} />
      ) : (
        <SelectCars
          userGroupId={userGroupId}
          loading={isSavedCarsLoading}
          savedCars={savedCars?.results}
          carSearch={carSearch}
          removeCar={removeCar}
          selectedCars={selectedCars}
        />
      )}

      {savedCars && savedCars.count > initialPageSize && (
        <NoddiBasicCard size='small' className='mt-4'>
          <TablePagination
            pagination={pagination}
            loading={isSavedCarsLoading}
            totalCount={savedCars.count}
            onChange={({ pageIndex, pageSize }) => setPagination({ pageIndex, pageSize, search: '' })}
            translations={getCommonTranslations().table}
          />
        </NoddiBasicCard>
      )}
    </BookingScreen>
  );
};
